import {createContext, PropsWithChildren, useContext, useMemo, useState} from 'react';
import {useLazyGetAssetOverallImpactQuery} from "../../redux/api/asset";
import {HazardTypeClass, OverallImpact} from "../../types/internal/Hazard";
import {CheckboxValueType} from "antd/lib/checkbox/Group";
import {CheckboxProps} from "antd";
import {useGetHazardTypeClassesQuery} from "../../redux/api/hazardTypes";


type HazardSelectionContextType = {
  getCalculation: ReturnType<typeof useLazyGetAssetOverallImpactQuery>[0],
  additionalOptionsGetCalculation: {
    data: OverallImpact | undefined,
    isFetching: boolean,
    isUninitialized: boolean,
    originalArgs: {
      assetUuid: string
      hazard_type_class_uuids?: string[] | undefined
    } | undefined
  },
  checkedList: CheckboxValueType[],
  checkAll: boolean,
  indeterminate: boolean,
  isNoSelection: boolean,
  onChange: (list: CheckboxValueType[]) => void,
  onCheckAllChange: CheckboxProps['onChange'],
  allTheOptions: {
    key: string,
    label: string,
    value: string
  }[],
  isFetchingHazardTypes: boolean,
  hazardTypes: HazardTypeClass[] | undefined
};

// Create the context with an initial default value of undefined
const HazardSelection = createContext<HazardSelectionContextType | undefined>(undefined);

export const HazardSelectionContext = ({children}: PropsWithChildren) => {

  const [getCalculation, {data, isFetching, isUninitialized, originalArgs}] = useLazyGetAssetOverallImpactQuery()
  const [checkedList, setCheckedList] = useState<CheckboxValueType[]>(originalArgs?.hazard_type_class_uuids ?? []);

  const {
    data: hazardTypes,
    isFetching: isFetchingHazardTypes,
  } = useGetHazardTypeClassesQuery();
  const allTheOptions = useMemo(() => {
    return hazardTypes?.filter(el => el.to_be_used_in_impact)
      .map(hazard => ({
      key: hazard.uuid,
      label: hazard.name,
      value: hazard.uuid
    })).sort((a, b) => a.label.localeCompare(b.label)) ?? []
  }, [hazardTypes])

  const checkAll = allTheOptions?.length === checkedList.length;
  const indeterminate = checkedList.length > 0 && checkedList.length < (allTheOptions?.length ?? 0);
  const onChange = (list: CheckboxValueType[]) => {
    setCheckedList(list);
  };
  const onCheckAllChange: CheckboxProps['onChange'] = (e) => {
    setCheckedList(e.target.checked ? allTheOptions.map(el => el.value) : []);
  };
  const isNoSelection = !checkedList.length

  return (
    <HazardSelection.Provider value={{
      checkedList,
      checkAll,
      indeterminate,
      isNoSelection,
      onChange,
      onCheckAllChange,
      allTheOptions,
      getCalculation,
      additionalOptionsGetCalculation: {data, isFetching, isUninitialized, originalArgs},
      hazardTypes,
      isFetchingHazardTypes,
    }}>
      {children}
    </HazardSelection.Provider>
  );
};

// Custom hook to consume the HazardSelectionContext
export const useHazardSelectionContext = () => {
  const context = useContext(HazardSelection);
  if (!context) {
    throw new Error('useHazardSelectionContext must be used within a HazardSelectionProvider');
  }
  return context;
};
